import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaMapMarkerAlt,
  FaPhone,
  FaUser,
  FaFlask,
  FaCreditCard,
  FaTruck,
  FaVial,
  FaMoneyBillWave,
  FaCheck,
} from "react-icons/fa";
import { APP_PATH } from "../../../api/backendPath";
import logo from "../../../assets/images/logo.png";
import styles from "../../../styles/__public_imaging_order_detail.module.scss";
import { formatCustom as tzCustom, formatDateNumeric as tzDateNumeric } from "../../../utils/datetime";
import { composeBookingChildName } from "../../../utils/childBooking";
import { formatPercent, formatWholeAmount } from "../../../utils/amountFormat";

const PublicOrderDetail = () => {
  const { t } = useTranslation();
  const { id: orderId } = useParams();

  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workflowStatus, setWorkflowStatus] = useState({
    technicianDispatched: false,
    sampleCollectionStarted: false,
    sampleCollected: false,
    paymentCollected: false,
  });
  const [estimatedArrivalMinutes, setEstimatedArrivalMinutes] = useState(30);

  const formatPaymentStatus = (paymentMethod, totalAmount) => {
    if (!paymentMethod || !totalAmount) return "Payment Pending";
    return paymentMethod === "COD"
      ? `COD: ${formatWholeAmount(totalAmount)} IQD`
      : `Online Payment: ${formatWholeAmount(totalAmount)} IQD`;
  };

  const startLiveNavigation = (address) => {
    if (!address || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const parts = [
          address.street,
          address.areaDistrict,
          address.city,
          address.governorate,
          address.country,
        ].filter(Boolean);
        const dest = parts.join(", ");
        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        if (isMobile) {
          window.location.href = `google.navigation:q=${encodeURIComponent(dest)}&mode=d`;
        } else {
          const { latitude, longitude } = position.coords;
          window.open(
            `https://www.google.com/maps/dir/${latitude},${longitude}/${encodeURIComponent(dest)}`,
            "_blank",
          );
        }
      },
      () => alert("Please allow location access for live navigation"),
      { enableHighAccuracy: true, timeout: 5000 },
    );
  };

  const handleWorkflowUpdate = async (action) => {
    try {
      const body = { action };
      if (action === "dispatch")
        body.estimatedArrivalMinutes = estimatedArrivalMinutes;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${APP_PATH.publicOrderDetails}/${orderId}/workflow`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      const data = await response.json();

      if (data.status) {
        await fetchOrderDetails();
        const messages = {
          dispatch: `Technician dispatched! Patient notified with ${estimatedArrivalMinutes} min ETA.`,
          start_sample_collection:
            "Procedure started. Status updated to In Progress.",
          complete_job_cod: "Cash collected and order completed!",
          complete_job_online: "Order completed!",
        };
        alert(messages[action] || "Workflow updated.");
      } else {
        alert(`Failed: ${data.message || "Unknown error"}`);
      }
    } catch (err) {
      alert("Failed to update workflow. Check your connection.");
    }
  };

  const formatPatientName = (p) => {
    if (!p) return "N/A";
    return (
      [p.full_name, p.surname].filter(Boolean).join(" ") ||
      "N/A"
    );
  };

  const formatTestList = (tests) => {
    if (!tests?.length) return "No tests specified";
    return tests.map((t) => t.test_name || t.testName).join(", ");
  };

  const fetchOrderDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${APP_PATH.publicOrderDetails}/${orderId}`,
      );
      const data = await response.json();

      // Check if access is denied for visit center orders
      if (!data.status && data.error_code === "ACCESS_DENIED_VISIT_CENTER") {
        setError({
          type: "access_denied",
          message:
            data.message ||
            "This page is only accessible for home visit orders.",
          bookingNumber: data.data?.bookingNumber,
          preferredLocation: data.data?.preferredLocation,
        });
        setLoading(false);
        return;
      }

      if (data.status && data.data) {
        setOrderDetails(data.data);
        if (data.data.workflowStatus)
          setWorkflowStatus(data.data.workflowStatus);
      } else {
        setError({ type: "not_found", message: "Order not found" });
      }
    } catch (err) {
      setError({ type: "error", message: "Failed to load order details" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  if (loading) {
    return (
      <div className={styles["public-container"]}>
        <div className={styles["loading-container"]}>
          <div className={styles["loading-spinner"]}></div>
          <p>Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error || !orderDetails) {
    return (
      <div className={styles["public-container"]}>
        <div className={styles["error-container"]}>
          {error?.type === "access_denied" ? (
            <>
              <div style={{ fontSize: "48px", marginBottom: "20px" }}>🚫</div>
              <h2>Access Restricted</h2>
              <p
                style={{
                  fontSize: "16px",
                  marginBottom: "10px",
                  fontWeight: "500",
                }}
              >
                {error.message}
              </p>
              {error.bookingNumber && (
                <p
                  style={{ fontSize: "14px", color: "#666", marginTop: "15px" }}
                >
                  Booking Number: <strong>{error.bookingNumber}</strong>
                </p>
              )}
              <div
                style={{
                  marginTop: "25px",
                  padding: "15px",
                  backgroundColor: "#f0f9ff",
                  borderRadius: "8px",
                  border: "1px solid #0ea5e9",
                }}
              >
                <p style={{ fontSize: "14px", color: "#0369a1", margin: 0 }}>
                  📍 This is an <strong>on-site/visit center</strong>{" "}
                  appointment. Please visit the imaging center at your scheduled
                  time.
                </p>
              </div>
            </>
          ) : (
            <>
              <h2>{error?.type === "not_found" ? "Not Found" : "Error"}</h2>
              <p>{error?.message || "Order details not found."}</p>
            </>
          )}
        </div>
      </div>
    );
  }

  // A booking refunded by the admin is moved to CANCELLED (or REJECTED). In that
  // state the technician must not take any further action. We detect the refund
  // case from the rejection reason to show a clear message.
  const orderStatusUpper = String(orderDetails.status || "").toUpperCase();
  const isRejectedOrCancelled =
    orderStatusUpper === "REJECTED" || orderStatusUpper === "CANCELLED";
  const rejectionReason = orderDetails.rejection_reason || "";
  const isRefundRejection =
    isRejectedOrCancelled && /refund/i.test(rejectionReason);

  return (
    <div className={styles["public-container"]}>
      <div className={styles["logo-header"]}>
        <img src={logo} alt="Tiryaq" />
      </div>

      <div className={styles["public-header"]}>
        <h1>Imaging Appointment Details</h1>
        <p>Booking ID: {orderDetails.bookingNumber}</p>
      </div>

      {isRejectedOrCancelled && (
        <div
          style={{
            margin: "0 auto 20px",
            maxWidth: "900px",
            padding: "16px 20px",
            backgroundColor: "#fef2f2",
            border: "1px solid #ef4444",
            borderRadius: "8px",
            color: "#b91c1c",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "18px", fontWeight: 700, marginBottom: "6px" }}>
            {isRefundRejection
              ? "Booking Refunded & Rejected"
              : "Booking Rejected"}
          </div>
          <p style={{ margin: 0, fontSize: "14px" }}>
            {isRefundRejection
              ? "This booking was rejected because the patient's payment has been refunded by the admin. No further action can be taken on this order."
              : "This booking has been rejected and can no longer be processed. No further action can be taken."}
          </p>
        </div>
      )}

      <div className={styles["public-content"]}>
        {/* Patient Information */}
        <div className={styles["detail-card"]}>
          <div className={styles["card-header"]}>
            <FaUser className={styles["card-icon"]} />
            <h2>Patient Information</h2>
          </div>
          <div className={styles["card-content"]}>
            {/* Child booking: the scan is for the CHILD, so the Name row shows the child
                with a "Child" pill and the account holder follows as the Guardian — the
                same layout as the lab technician page. Self booking: unchanged. */}
            {composeBookingChildName(orderDetails) ? (
              <>
                <div className={styles["info-row"]}>
                  <span className={styles["info-label"]}>Name:</span>
                  <span
                    className={styles["info-value"]}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span>{composeBookingChildName(orderDetails)}</span>
                    <span
                      style={{
                        fontSize: "11px",
                        background: "#0cbdbd",
                        color: "#fff",
                        padding: "2px 8px",
                        borderRadius: "10px",
                        fontWeight: 500,
                      }}
                    >
                      Child
                    </span>
                  </span>
                </div>
                <div className={styles["info-row"]}>
                  <span className={styles["info-label"]}>Guardian Name:</span>
                  <span className={styles["info-value"]}>
                    {formatPatientName(orderDetails.patient)}
                  </span>
                </div>
              </>
            ) : (
              <div className={styles["info-row"]}>
                <span className={styles["info-label"]}>Name:</span>
                <span className={styles["info-value"]}>
                  {formatPatientName(orderDetails.patient)}
                </span>
              </div>
            )}
            <div className={styles["info-row"]}>
              <span className={styles["info-label"]}>Contact:</span>
              <span className={styles["info-value"]}>
                <FaPhone className={styles["inline-icon"]} />
                {orderDetails.patient?.mobile_number || "N/A"}
              </span>
            </div>
          </div>
        </div>

        {/* Test Information */}
        <div className={styles["detail-card"]}>
          <div className={styles["card-header"]}>
            <FaFlask className={styles["card-icon"]} />
            <h2>Test Information</h2>
          </div>
          <div className={styles["card-content"]}>
            <div className={styles["info-row"]}>
              <span className={styles["info-label"]}>Tests:</span>
              <span className={styles["info-value"]}>
                {formatTestList(orderDetails.tests)}
              </span>
            </div>
            <div className={styles["info-row"]}>
              <span className={styles["info-label"]}>Service Type:</span>
              <span className={styles["info-value"]}>Home Visit</span>
            </div>
          </div>
        </div>

        {/* Location */}
        <div className={styles["detail-card"]}>
          <div className={styles["card-header"]}>
            <FaMapMarkerAlt className={styles["card-icon"]} />
            <h2>Location</h2>
          </div>
          <div className={styles["card-content"]}>
            {orderDetails.patientAddress ? (
              <>
                <div className={styles["info-row"]}>
                  <span className={styles["info-label"]}>Address:</span>
                  <span className={styles["info-value"]}>
                    {[
                      orderDetails.patientAddress.houseFlatFloorNo,
                      orderDetails.patientAddress.street,
                      orderDetails.patientAddress.areaDistrict,
                      orderDetails.patientAddress.city,
                      orderDetails.patientAddress.governorate,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </div>
                <div className={styles["info-row"]}>
                  <span className={styles["info-label"]}>Navigation:</span>
                  <span className={styles["info-value"]}>
                    <button
                      onClick={() =>
                        startLiveNavigation(orderDetails.patientAddress)
                      }
                      className={styles["maps-link"]}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                      }}
                    >
                      <FaMapMarkerAlt className={styles["inline-icon"]} /> Live
                      Navigation
                    </button>
                  </span>
                </div>
              </>
            ) : (
              <div className={styles["info-row"]}>
                <span className={styles["info-value"]}>
                  Visit Center - Please visit our imaging facility
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Payment */}
        <div className={styles["detail-card"]}>
          <div className={styles["card-header"]}>
            <FaCreditCard className={styles["card-icon"]} />
            <h2>Payment Information</h2>
          </div>
          <div className={styles["card-content"]}>
            <div className={styles["info-row"]}>
              <span className={styles["info-label"]}>Status:</span>
              <span className={styles["info-value"]}>
                {formatPaymentStatus(
                  orderDetails.paymentMethod,
                  orderDetails.totalAmount,
                )}
              </span>
            </div>
            {orderDetails.pricing && (
              <>
                <div className={styles["info-row"]}>
                  <span className={styles["info-label"]}>Service Price:</span>
                  <span className={styles["info-value"]}>
                    {formatWholeAmount(orderDetails.pricing.subtotal || 0)}{" "}
                    IQD
                  </span>
                </div>
                {orderDetails.pricing.transportationFee > 0 && (
                  <div className={styles["info-row"]}>
                    <span className={styles["info-label"]}>Transportation Fee:</span>
                    <span className={styles["info-value"]}>
                      {formatWholeAmount(orderDetails.pricing.transportationFee)}{" "}
                      IQD
                    </span>
                  </div>
                )}
                {orderDetails.pricing.adminFee > 0 && (
                  <div className={styles["info-row"]}>
                    <span className={styles["info-label"]}>
                      Admin Fee
                      {orderDetails.pricing.adminFeePercentage
                        ? ` (${formatPercent(orderDetails.pricing.adminFeePercentage)}%)`
                        : ""}
                      :
                    </span>
                    <span className={styles["info-value"]}>
                      {formatWholeAmount(orderDetails.pricing.adminFee)}{" "}
                      IQD
                    </span>
                  </div>
                )}
                {/* Loyalty points the patient redeemed — shown as a deduction (green, like
                    the printed invoice) so this breakdown's Total matches the invoice and the
                    amount actually paid. Sourced from the booking's persisted
                    `loyalty_discount`, surfaced on `pricing` by the public endpoint. */}
                {parseFloat(orderDetails.pricing.loyaltyDiscount || 0) > 0 && (
                  <div className={styles["info-row"]}>
                    <span className={styles["info-label"]}>
                      Loyalty Point Discount
                      {orderDetails.pricing.loyaltyPointsUsed
                        ? ` (${orderDetails.pricing.loyaltyPointsUsed} pts)`
                        : ""}
                      :
                    </span>
                    <span
                      className={styles["info-value"]}
                      style={{ color: "#00BC7D" }}
                    >
                      −{" "}
                      {formatWholeAmount(orderDetails.pricing.loyaltyDiscount)}{" "}
                      IQD
                    </span>
                  </div>
                )}
                <div className={styles["info-row"]}>
                  <span className={styles["info-label"]}>
                    <strong>Total:</strong>
                  </span>
                  <span className={styles["info-value"]}>
                    <strong>
                      {formatWholeAmount(
                        orderDetails.pricing.total ||
                          orderDetails.totalAmount ||
                          0,
                      )}{" "}
                      IQD
                    </strong>
                  </span>
                </div>
              </>
            )}
            {orderDetails.scheduledDate && (
              <div className={styles["info-row"]}>
                <span className={styles["info-label"]}>Scheduled Date:</span>
                <span className={styles["info-value"]}>
                  {tzDateNumeric(new Date(orderDetails.scheduledDate))}
                </span>
              </div>
            )}
            {orderDetails.scheduledTime && (
              <div className={styles["info-row"]}>
                <span className={styles["info-label"]}>Scheduled Time:</span>
                <span className={styles["info-value"]}>
                  {orderDetails.scheduledTime}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Technician Actions */}
        <div className={`${styles["detail-card"]} ${styles["card-span-full"]}`}>
          <div className={styles["card-header"]}>
            <FaTruck className={styles["card-icon"]} />
            <h2>Technician Actions</h2>
          </div>
          <div className={styles["card-content"]}>
            {isRejectedOrCancelled ? (
              <div
                style={{
                  padding: "16px 20px",
                  backgroundColor: "#fef2f2",
                  border: "1px solid #ef4444",
                  borderRadius: "8px",
                  color: "#b91c1c",
                  textAlign: "center",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {isRefundRejection
                  ? "The patient's payment has been refunded by the admin and this booking is now rejected. Technician actions are disabled."
                  : "This booking has been rejected. Technician actions are disabled."}
              </div>
            ) : (
              <>
            {!workflowStatus.technicianDispatched && (
              <div className={styles["arrival-time-section"]}>
                <div className={styles["arrival-time-row"]}>
                  <label className={styles["arrival-label"]}>
                    Estimated Arrival Time:
                  </label>
                  <div className={styles["arrival-input-container"]}>
                    <input
                      type="number"
                      min="5"
                      max="120"
                      value={estimatedArrivalMinutes}
                      onChange={(e) =>
                        setEstimatedArrivalMinutes(
                          parseInt(e.target.value) || 30,
                        )
                      }
                      className={styles["arrival-time-input"]}
                    />
                    <span className={styles["arrival-time-unit"]}>minutes</span>
                  </div>
                </div>
              </div>
            )}

            <div className={styles["action-buttons"]}>
              <button
                onClick={() => handleWorkflowUpdate("dispatch")}
                disabled={workflowStatus.technicianDispatched}
                className={`${styles["action-btn"]} ${workflowStatus.technicianDispatched ? styles["completed"] : styles["pending"]}`}
              >
                {workflowStatus.technicianDispatched ? (
                  <FaCheck className={styles["btn-icon"]} />
                ) : (
                  <FaTruck className={styles["btn-icon"]} />
                )}
                {workflowStatus.technicianDispatched
                  ? "Technician Dispatched ✓"
                  : "Mark as Dispatched"}
              </button>

              <button
                onClick={() => handleWorkflowUpdate("start_sample_collection")}
                disabled={
                  !workflowStatus.technicianDispatched ||
                  workflowStatus.sampleCollectionStarted
                }
                className={`${styles["action-btn"]} ${workflowStatus.sampleCollectionStarted ? styles["completed"] : workflowStatus.technicianDispatched ? styles["active"] : styles["disabled"]}`}
              >
                {workflowStatus.sampleCollectionStarted ? (
                  <FaCheck className={styles["btn-icon"]} />
                ) : (
                  <FaVial className={styles["btn-icon"]} />
                )}
                {workflowStatus.sampleCollectionStarted
                  ? "Procedure Started ✓"
                  : "Start Procedure"}
              </button>

              {orderDetails.paymentMethod === "COD" ||
              orderDetails.paymentStatus === "PENDING" ? (
                <button
                  onClick={() => handleWorkflowUpdate("complete_job_cod")}
                  disabled={
                    !workflowStatus.sampleCollectionStarted ||
                    workflowStatus.paymentCollected
                  }
                  className={`${styles["action-btn"]} ${workflowStatus.paymentCollected ? styles["completed"] : workflowStatus.sampleCollectionStarted ? styles["active"] : styles["disabled"]}`}
                >
                  {workflowStatus.paymentCollected ? (
                    <FaCheck className={styles["btn-icon"]} />
                  ) : (
                    <FaMoneyBillWave className={styles["btn-icon"]} />
                  )}
                  {workflowStatus.paymentCollected
                    ? "Cash Collected & Done ✓"
                    : "Mark Cash Collected and Done"}
                </button>
              ) : (
                <button
                  onClick={() => handleWorkflowUpdate("complete_job_online")}
                  disabled={
                    !workflowStatus.sampleCollectionStarted ||
                    workflowStatus.sampleCollected
                  }
                  className={`${styles["action-btn"]} ${workflowStatus.sampleCollected ? styles["completed"] : workflowStatus.sampleCollectionStarted ? styles["active"] : styles["disabled"]}`}
                >
                  {workflowStatus.sampleCollected ? (
                    <FaCheck className={styles["btn-icon"]} />
                  ) : (
                    <FaCheck className={styles["btn-icon"]} />
                  )}
                  {workflowStatus.sampleCollected
                    ? "Order Completed ✓"
                    : "Mark Order Completed"}
                </button>
              )}
            </div>
              </>
            )}

            {orderDetails.status === "COMPLETED" && (
              <div className={styles["completion-message"]}>
                <FaCheck className={styles["completion-icon"]} />
                <span>Order completed successfully!</span>
              </div>
            )}

            {workflowStatus.technicianDispatched &&
              orderDetails.timestamps?.estimatedArrivalTime &&
              orderDetails.status !== "COMPLETED" && (
                <div className={styles["arrival-time-display"]}>
                  <FaTruck className={styles["inline-icon"]} />
                  <span>
                    Estimated Arrival:{" "}
                    {tzCustom(new Date(
                      orderDetails.timestamps.estimatedArrivalTime,
                    ), {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              )}

            <div className={styles["status-info"]}>
              <div className={styles["info-row"]}>
                <span className={styles["info-label"]}>Current Status:</span>
                <span
                  className={`${styles["info-value"]} ${styles["status-badge"]}`}
                >
                  {orderDetails.status || "PENDING"}
                </span>
              </div>
              {orderDetails.paymentStatus && (
                <div className={styles["info-row"]}>
                  <span className={styles["info-label"]}>Payment Status:</span>
                  <span
                    className={`${styles["info-value"]} ${styles["payment-badge"]}`}
                  >
                    {orderDetails.paymentStatus}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles["public-footer"]}>
        <p>For any inquiries, please contact our support team.</p>
      </div>
    </div>
  );
};

export default PublicOrderDetail;
